/**
 * Shared YYYY-MM-DD string <-> Date helpers, used by every packages/ui
 * component that stores dates as plain strings (DatePickerButton,
 * DateRangePill). Keeping one copy avoids the parse/format guards drifting
 * between components.
 */
import type { Matcher } from 'react-day-picker';

/** Parse a YYYY-MM-DD string into a local Date, or undefined when empty/invalid. */
export function parseDateStr(str: string): Date | undefined {
  if (!str) return undefined;
  const [y, m, d] = str.split('-').map(Number);
  if (!y || !m || !d) return undefined;
  return new Date(y, m - 1, d);
}

/** Format a Date into a YYYY-MM-DD string. */
export function toDateStr(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Builds the `Matcher[]` used to disable days before `minDate`/after
 * `maxDate` (both YYYY-MM-DD strings) in a react-day-picker `Calendar`.
 * Shared by `DatePickerButton` and `DateRangeButtonField` so their
 * range-constraint semantics can't drift apart.
 */
export function buildDateRangeMatchers(minDate?: string, maxDate?: string): Matcher[] {
  const matchers: Matcher[] = [];
  const minDateObj = parseDateStr(minDate ?? '');
  const maxDateObj = parseDateStr(maxDate ?? '');
  if (minDateObj) matchers.push({ before: minDateObj });
  if (maxDateObj) matchers.push({ after: maxDateObj });
  return matchers;
}
